import React, { useState, useEffect } from 'react'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import DataTable from 'aldoo-ra/DataTable'
import Checkbox from 'aldoo-ra/Checkbox'
import Badge from 'aldoo-ra/Badge'
import DropdownSelect from 'aldoo-ra/DropdownSelect'
import Tooltip from 'aldoo-ra/Tooltip'
import { showNotification } from 'aldoo-ra/Notification'
import { showPromptDialog } from 'aldoo-ra/PromptDialog'
import AldooComponent from 'aldoo-ra/AldooComponent'
import { GetCMSContent, SaveCMSContent, DeleteCMSContent } from 'aldoo-ra/CMS/api'
import { url } from 'aldoo-ra/CMS/url'
import {
  PlusCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import AldooComponentDialog from './AldooComponentDialog'

const Components = () => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [categoryFilter, setCategoryFilter] = useState([{ value: 'all', label: 'All Categories' }])
  const [statusFilter, setStatusFilter] = useState([{ value: 'all', label: 'All Status' }])
  const [showDialog, setShowDialog] = useState(false)
  const [editingComponent, setEditingComponent] = useState(null)
  const [syncing, setSyncing] = useState(false)

  // Sync components from registry to CMS
  const handleSyncComponents = async () => {
    setSyncing(true)
    try {
      const registeredComponents = AldooComponent.getComponents()

      // Get existing components from CMS
      const cmsResponse = await GetCMSContent({
        match: {
          'content.type': 'AldooComponent',
          'deleted': { $ne: true }
        },
        itemsPerPage: -1
      })

      const existingComponents = cmsResponse.result ? cmsResponse.data : []

      // Filter out deleted entries and create map with only non-deleted components
      const existingComponentsMap = new Map(
        existingComponents
          .filter(c => !c.deleted)
          .map(c => [c.content?.id, c])
      )

      // Create a map of registry components for status checking
      const registryMap = new Map(registeredComponents.map(c => [c.id, c]))

      let syncedCount = 0
      let errorCount = 0

      // Sync each registered component (mark as active)
      for (const component of registeredComponents) {
        try {
          // Double-check for existing component with this exact ID (including deleted ones)
          const checkResponse = await GetCMSContent({
            match: {
              'content.type': 'AldooComponent',
              'content.id': component.id,
              'deleted': { $ne: true }
            },
            itemsPerPage: 1
          })

          const existing = checkResponse.result && checkResponse.data && checkResponse.data.length > 0
            ? checkResponse.data[0]
            : existingComponentsMap.get(component.id)

          // Convert input format: {banner: {type, label, default}} to {banner: default_value}
          // Use "__empty__" placeholder for empty strings since API sanitize removes empty strings
          let processedInput = {}
          if (component.input && typeof component.input === 'object') {
            Object.keys(component.input).forEach(key => {
              const inputDef = component.input[key]
              if (inputDef && typeof inputDef === 'object') {
                const defaultValue = inputDef.default !== undefined ? inputDef.default : ""
                processedInput[key] = defaultValue === "" ? "__empty__" : defaultValue
              } else {
                processedInput[key] = inputDef === "" ? "__empty__" : inputDef
              }
            })
          }

          console.log(`Component ${component.id} - Original input:`, component.input)
          console.log(`Component ${component.id} - Processed input:`, processedInput)

          // Build the component data in the format CMS expects
          const contentData = {
            type: 'AldooComponent',
            id: component.id,
            name: component.meta?.name || component.id,
            description: component.description || component.meta?.description || '',
            category: component.meta?.category ? [component.meta.category] : [],
            icon: component.meta?.icon || null,
            input: processedInput,
            status: 'active'
          }

          const savePayload = {
            content: contentData
          }

          if (existing) {
            // Update existing - include _id at root level and merge with existing data
            // Override with source code data (description, name, etc.)
            savePayload._id = existing._id
            savePayload.content = {
              ...existing.content,
              ...contentData,
              // Always update these from source
              description: component.description || component.meta?.description || existing.content.description || '',
              name: component.meta?.name || component.id,
              input: processedInput,
            }
            console.log(`Updating existing component: ${component.id} (_id: ${existing._id})`)
          } else {
            console.log(`Creating new component: ${component.id}`)
          }

          await SaveCMSContent({ payload: savePayload })
          syncedCount++
        } catch (error) {
          console.error(`Failed to sync component ${component.id}:`, error)
          errorCount++
        }
      }

      // Mark components in CMS that are no longer in registry as missing
      for (const existing of existingComponents) {
        if (!registryMap.has(existing.content?.id)) {
          try {
            const contentData = {
              ...existing.content,
              status: 'missing'
            }
            const savePayload = {
              content: contentData,
              _id: existing._id
            }
            await SaveCMSContent({ payload: savePayload })
          } catch (error) {
            console.error(`Failed to update status for ${existing.content?.id}:`, error)
          }
        }
      }

      showNotification({
        message: `Synced ${syncedCount} component${syncedCount !== 1 ? 's' : ''} to CMS${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
        className: errorCount > 0 ? 'bg-admin_warning text-white' : 'bg-admin_primary text-admin_text_inv'
      })

      setRefreshKey(prev => prev + 1)
    } catch (error) {
      showNotification({
        message: 'Failed to sync components',
        className: 'bg-admin_error text-white'
      })
    } finally {
      setSyncing(false)
    }
  }

  // API method to get components from CMS
  const getComponents = async (requestFields = {}) => {
    try {
      const response = await GetCMSContent({
        ...requestFields,
        match: {
          'content.type': 'AldooComponent',
          ...requestFields.match
        }
      })

      if (!response.result) {
        return { result: false, data: [], totalItems: 0 }
      }

      return {
        result: true,
        data: response.data,
        totalItems: response.totalItems || response.data.length
      }
    } catch (error) {
      console.error('Failed to fetch components:', error)
      return { result: false, data: [], totalItems: 0 }
    }
  }

  // Build request fields based on filters
  const getRequestFields = () => {
    const match = {}

    // Category filter
    const selectedCategory = categoryFilter[0]?.value
    if (selectedCategory && selectedCategory !== 'all') {
      match['content.category'] = selectedCategory
    }

    // Status filter
    const selectedStatus = statusFilter[0]?.value
    if (selectedStatus && selectedStatus !== 'all') {
      match['content.status'] = selectedStatus
    }

    return { match }
  }

  // Handle select all
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map(item => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems(prev => [...prev, itemId])
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId))
    }
  }

  // Handle bulk delete
  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) return

    showPromptDialog({
      title: 'Delete Components',
      message: `Are you sure you want to delete ${selectedItems.length} component${selectedItems.length === 1 ? '' : 's'}? This will only remove them from the CMS, not the source code.`,
      confirmText: 'Delete',
      confirmVariant: 'error_m',
      onConfirm: async () => {
        try {
          let deletedCount = 0
          for (const id of selectedItems) {
            const response = await DeleteCMSContent({ itemId: id })
            if (response.result) deletedCount++
          }

          showNotification({
            message: `Deleted ${deletedCount} component${deletedCount === 1 ? '' : 's'}`,
            className: 'bg-admin_primary text-admin_text_inv'
          })

          setSelectedItems([])
          setRefreshKey(prev => prev + 1)
        } catch (error) {
          showNotification({
            message: 'Failed to delete components',
            className: 'bg-admin_error text-white'
          })
        }
      }
    })
  }

  // Handle edit component
  const handleEditComponent = (component) => {
    setEditingComponent(component)
    setShowDialog(true)
  }

  // Handle create component
  const handleCreateComponent = () => {
    setEditingComponent(null)
    setShowDialog(true)
  }

  // Handle delete component
  const handleDeleteComponent = (component) => {
    showPromptDialog({
      title: 'Delete Component',
      message: `Are you sure you want to delete component "${component.content?.name || component.content?.id}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'error_m',
      onConfirm: async () => {
        try {
          const response = await DeleteCMSContent({ itemId: component._id })

          if (response.result) {
            showNotification({
              message: `Component "${component.content?.name || component.content?.id}" deleted successfully`,
              className: 'bg-admin_primary text-admin_text_inv'
            })
            setRefreshKey(prev => prev + 1)
          } else {
            showNotification({
              message: response.error || 'Failed to delete component',
              className: 'bg-admin_error text-white'
            })
          }
        } catch (error) {
          showNotification({
            message: 'An error occurred while deleting the component',
            className: 'bg-admin_error text-white'
          })
        }
      }
    })
  }

  // Handle dialog close
  const handleDialogClose = () => {
    setShowDialog(false)
    setEditingComponent(null)
    setRefreshKey(prev => prev + 1)
  }

  // Get unique categories from registry
  const getCategories = () => {
    const registryComponents = AldooComponent.getComponents()
    const categories = new Set(
      registryComponents
        .map(c => c.meta?.category)
        .filter(Boolean)
    )
    return [
      { value: 'all', label: 'All Categories' },
      ...Array.from(categories).map(cat => ({ value: cat, label: cat }))
    ]
  }

  // Column definitions
  const columns = [
    {
      id: 'select',
      headerClassName: 'w-8',
      header: (value, items) => (
        <Checkbox
          variant="admin_compact"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin_compact"
          isOn={selectedItems.includes(item._id)}
          onChange={(isSelected) => handleSelectItem(item._id, isSelected)}
        />
      ),
    },
    {
      id: 'component',
      title: 'Component',
      headerClassName: 'w-1/3',
      render: (_, item) => {
        const icon = item.content?.icon
        const iconUrl = typeof icon === 'string' && icon ? url(icon) : null

        return (
          <div className="flex items-center space-x-3 py-2">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={item.content?.name}
                className="w-8 h-8 object-contain flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded bg-admin_neutral_1 flex items-center justify-center flex-shrink-0">
                <Typography variant="admin_txt2" className="text-admin_text_secondary">
                  🧩
                </Typography>
              </div>
            )}
            <Typography variant="admin_label" className="truncate my-0">
              {item.content?.name || item.content?.id}
            </Typography>
          </div>
        )
      },
    },
    {
      id: 'description',
      title: 'Description',
      headerClassName: 'w-1/3',
      render: (_, item) => (
        <Typography variant="admin_txt1" className="text-admin_text_secondary line-clamp-2">
          {item.content?.description || 'No description'}
        </Typography>
      ),
    },
    {
      id: 'category',
      title: 'Category',
      headerClassName: 'w-[120px]',
      render: (_, item) => {
        const categories = item.content?.category || []
        return categories.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {categories.map((cat, idx) => (
              <Badge key={idx} variant="admin_neutral">
                {typeof cat === 'string' ? cat : cat.name || cat.value || cat}
              </Badge>
            ))}
          </div>
        ) : (
          <Typography variant="admin_txt1_dim">
            Uncategorized
          </Typography>
        )
      },
    },
    {
      id: 'status',
      title: 'Status',
      headerClassName: 'w-[100px] text-center',
      render: (_, item) => {
        const status = item.content?.status
        return status === 'active' ? (
          <Tooltip content="Implementation found in source code">
            <Badge variant="admin_success" className="flex items-center gap-1">
              <CheckCircleIcon className="w-3 h-3" />
              Active
            </Badge>
          </Tooltip>
        ) : (
          <Tooltip content="No implementation found - component may have been removed from source">
            <Badge variant="admin_warning" className="flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3 h-3" />
              Missing
            </Badge>
          </Tooltip>
        )
      },
    },
    {
      id: 'actions',
      title: 'Actions',
      headerClassName: 'w-[100px] text-center',
      render: (_, item) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip content="Edit component">
            <button
              onClick={() => handleEditComponent(item)}
              className="p-1.5 rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_primary transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip content="Delete component">
            <button
              onClick={() => handleDeleteComponent(item)}
              className="p-1.5 rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_error transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ]

  // Header controls
  const headerControls = (
    <div className="w-full flex items-end gap-4">
      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Category
        </Typography>
        <DropdownSelect
          variant="admin"
          options={getCategories()}
          value={categoryFilter}
          onChange={(values) => {
            setCategoryFilter(values?.length ? values : [{ value: 'all', label: 'All Categories' }])
            setRefreshKey(prev => prev + 1)
          }}
          fixedWidth="150px"
          showCheckbox={false}
        />
      </div>

      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Status
        </Typography>
        <DropdownSelect
          variant="admin"
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'active', label: 'Active' },
            { value: 'missing', label: 'Missing Implementation' }
          ]}
          value={statusFilter}
          onChange={(values) => {
            setStatusFilter(values?.length ? values : [{ value: 'all', label: 'All Status' }])
            setRefreshKey(prev => prev + 1)
          }}
          fixedWidth="180px"
          showCheckbox={false}
        />
      </div>
    </div>
  )

  // Table footer
  const tableFooter = selectedItems.length > 0 ? (
    <div className="flex items-center justify-between p-4 border-t border-admin_bordered">
      <Typography variant="admin_body">
        {selectedItems.length} component{selectedItems.length !== 1 ? 's' : ''} selected
      </Typography>

      <div className="flex items-center gap-2">
        <Button variant="admin_secondary" onClick={() => setSelectedItems([])}>
          Deselect
        </Button>
        <Button variant="admin_error" onClick={handleDeleteSelected}>
          <TrashIcon className="w-4 h-4 mr-1" />
          Delete
        </Button>
      </div>
    </div>
  ) : null

  return (
    <div className="p-6">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography component="h1" variant="admin_h3">
          Components
        </Typography>

        <div className="flex items-end justify-end gap-3">
          <Button
            variant="admin_secondary"
            onClick={handleSyncComponents}
            loading={syncing}
            disabled={syncing}
          >
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Sync Components
          </Button>

          <Button
            variant="admin_primary"
            onClick={handleCreateComponent}
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      <DataTable
        variant="admin_browser"
        apiMethod={getComponents}
        requestFields={getRequestFields()}
        headerControls={headerControls}
        tableFooter={tableFooter}
        searchField="content.id content.name content.description"
        itemsPerPage={25}
        refreshKey={refreshKey}
        preferredHeight="70vh"
        localUserPreferencesKey="admin.structure.components"
        styleOverrides={{
          table: 'w-full table-fixed',
          header: 'w-full flex justify-between items-end gap-2 -mt-5 mb-4',
          container: 'relative w-full flex flex-col h-[90vh] justify-start bg-admin_paper rounded-3xl px-6 pt-8 pb-6',
          searchInput: 'absolute w-[350px] h-[30px] top-6 right-4 text-text bg-transparent outline-none flex-1 px-2 py-1',
        }}
        renders={columns}
        noData={
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🧩</div>
            <Typography variant="admin_h4" className="text-admin_text_secondary mb-2">
              No components found
            </Typography>
            <Typography variant="admin_txt1_dim" className="mb-4">
              Sync components from source code or create a new one
            </Typography>
            <Button variant="admin_primary" onClick={handleSyncComponents} loading={syncing}>
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Sync Components
            </Button>
          </div>
        }
      />

      {showDialog && (
        <AldooComponentDialog
          isOpen={showDialog}
          onClose={handleDialogClose}
          component={editingComponent}
        />
      )}
    </div>
  )
}

export default Components